/**
 * Shared helpers for the study-groups sub-modules.
 */

import { buildWebLink } from '@flashka/shared/deep-links';
import type { SyncGroupMembership } from '@flashka/shared/sync-engine/schema/entities';
import { CARD_FIELD_SCOPES } from '@flashka/shared/sync-engine/schema/field-scopes';
import type { SGDeckProps } from '@flashka/shared/sync-engine/schema/group-entities';
import type { SyncCardProps } from '@flashka/shared/sync-engine/schema/shared-types';

import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { enqueueForGroup } from '../internal/hook/study-group-sender-registry';
import { useFlashkaStore } from '../internal/store/flashka-store';
import { useStudyGroupStore } from '../internal/store/study-group-store';
import { generateMutationId } from '../internal/sync-bookkeeping/pending-mutations';
import { getPendingMutationsTracker } from '../internal/sync-bookkeeping/pending-mutations-singleton';
import { getPreImageStash } from '../internal/sync-bookkeeping/pre-image-stash';

export function currentUserId(): string {
  return useFlashkaStore.getState().currentUserId ?? '';
}

export function nowISO(): string {
  return new Date().toISOString();
}

/**
 * Build a study-group invite URL.
 *
 * `origin` is the deployment's web origin — pass `FLASHKA_WEB_ORIGIN` for a
 * link that will be SENT to someone, and
 * `window.location.origin` only to keep a preview or localhost self-consistent.
 * The server uses `groupId` to route to the right Durable Object, then
 * validates `token` against `sgMeta.inviteToken` on the DO side.
 *
 * Shape and encoding come from the shared link registry, because this link is
 * the one users actually tap: the mobile app claims `/study-groups/*` as a
 * universal link and re-parses it with the same table, so an invite minted here
 * and an invite opened there cannot disagree about what it means.
 */
export function buildInviteUrl(
  origin: string,
  groupId: string,
  inviteToken: string,
  /** The SHARER's UI language: a link-preview scraper sends no locale, so
   *  the invite's preview (`flashka-web/src/seo/invite-og.ts`) reads it from
   *  the URL. Ignored by both clients when the link is opened. */
  lang?: string,
): string {
  return buildWebLink(
    'studyGroupInvite',
    { groupId },
    { token: inviteToken, lang: lang?.split('-')[0] || undefined },
    origin,
  );
}

/**
 * Send a JSON message over the group's WebSocket.
 *
 * `enqueueForGroup` sends right away if the socket is connected;
 * otherwise it buffers the message and flushes on sender register.
 * This makes "user clicks Add Deck before the WS is up" instant
 * instead of waiting for the sync-init dirty-flush round trip.
 */
export function sendSG(groupId: string, msg: Record<string, unknown>): void {
  enqueueForGroup(groupId, JSON.stringify(msg));
}

/**
 * Send an SG mutation envelope with a fresh `mutationId` AND register
 * a pre-image revert thunk in the stash (Phase E). The server replies
 * with `op-ack` (drop the stash) or `op-rejected` (consume + revert
 * the local state).
 *
 * `expectedScopes` is what the tracker waits for before it considers
 * the mutation fully settled. SG cards/properties writes are always
 * single-scope `['shared']` from the perspective of this socket —
 * the per-member fan-out into UserDos goes through cross-DO RPC and
 * carries its own ack channel.
 *
 * Returns the generated `mutationId` so callers can correlate later
 * (e.g. a UI snapshot test that asserts the toast was tied to the
 * right edit).
 */
export function sendSGWithPreImage(
  groupId: string,
  msg: Record<string, unknown>,
  preImage: { scopes: ('shared' | 'personal')[]; revert: () => void },
): string {
  const mutationId = generateMutationId();
  const tracker = getPendingMutationsTracker();
  tracker.register(mutationId, preImage.scopes);
  getPreImageStash().register(mutationId, preImage.revert);
  // `markSent` re-bases the 5s timeout clock to the moment the frame
  // actually hits the wire (synchronous send OR outbox drain) — batching
  // / offline-outbox time no longer counts against the timeout.
  enqueueForGroup(groupId, JSON.stringify({ ...msg, mutationId }), () => {
    tracker.markSent(mutationId);
  });
  return mutationId;
}

/**
 * Optimistically apply a partial deck-properties patch and ship it
 * over the group socket using the framework's per-row record wire
 * (`sg-deckProperties-push`). Single entry point so every shared-
 * scope mutation lands on the same dispatch path the reconnect-time
 * `flushLocalDirty` already emits.
 *
 * Stamps `updatedAtISO` so the per-row LWW gate sees a strictly
 * monotonic timestamp, and registers a pre-image revert thunk so an
 * `op-rejected` from the server rolls the local row back.
 *
 * No-ops + returns `null` when the deck row is absent from the local
 * store (caller raced a remove). Otherwise returns the mutation id
 * so tests can correlate.
 */
export function updateDeckProperty(
  groupId: string,
  deckId: string,
  patch: Partial<SGDeckProps>,
): string | null {
  const current = useStudyGroupStore.getState().groups[groupId]?.deckProps[deckId];
  if (!current) return null;
  const now = nowISO();
  const next: SGDeckProps = {
    ...current,
    ...patch,
    id: current.id,
    updatedAtISO: now,
  };
  useStudyGroupStore.getState().mergeDeckProps(groupId, [next]);
  return sendSGWithPreImage(
    groupId,
    {
      type: 'sg-deckProperties-push',
      key: next.id,
      entry: next,
      updatedAtISO: now,
    },
    {
      scopes: ['shared'],
      revert: () => useStudyGroupStore.getState().mergeDeckProps(groupId, [current]),
    },
  );
}

/**
 * Strip personal-scope fields off a card before pushing it to a study
 * group. Without this, the original sg-deck-push at share time leaks
 * the owner's `studyState`, `actions`, `starred`, `notes`, `tags`,
 * `chat`, and `personalUpdatedAtISO` to every other member of the
 * group — they'd open the deck and see "27 new / 7 to review / 2
 * favorites" inherited from the owner. Also drops any non-card key
 * (the `deckName` routing tag from `use-load-sg-folder-decks.ts:355`
 * is the historical offender) so the SG side doesn't choke later.
 *
 * Identity-scope fields (`id`) MUST be preserved — StudyGroupDo
 * stores each card at `sgcard\x01{deckId}\x01{cardId}` and rejects
 * the push with "Invalid card id" if `card.id` is missing (see
 * `group/validation.ts` SG_STORAGE_KEY_RULES). Matches the
 * identity-plus-shared projection that `projectEntity('group', …)`
 * applies on the server side.
 *
 * The structurally-required `actions` and `studyState` arrays are
 * defaulted to `[]` so the SG-side row passes type checks and any
 * later read (composed selector, archive snapshot) sees a clean
 * starting point per member.
 */
export function stripCardForSG(card: SyncCardProps): SyncCardProps {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(card)) {
    const scope = CARD_FIELD_SCOPES[key as keyof SyncCardProps];
    if (scope !== 'shared' && scope !== 'identity') continue;
    if (value === undefined) continue;
    out[key] = value;
  }
  // Required fields default to empty per-member starting state.
  if (!('actions' in out)) out.actions = [];
  if (!('studyState' in out)) out.studyState = [];
  return out as SyncCardProps;
}

// ---------------------------------------------------------------------------
// Dashboard hooks
// ---------------------------------------------------------------------------

export function useGroupMemberships(): SyncGroupMembership[] {
  return useFlashkaStore(
    useShallow((s) => {
      const rec = (s as unknown as { groupMemberships?: SyncGroupMembership[] })
        .groupMemberships;
      return Array.isArray(rec) ? rec : [];
    }),
  );
}

/** The viewer's memberships as the store holds them right now (outside React). */
export function getGroupMembershipsNow(): SyncGroupMembership[] {
  const rec = (
    useFlashkaStore.getState() as unknown as { groupMemberships?: SyncGroupMembership[] }
  ).groupMemberships;
  return Array.isArray(rec) ? rec : [];
}

export function useActiveGroupMemberships(): SyncGroupMembership[] {
  const all = useGroupMemberships();
  return useMemo(() => all.filter((m) => !m.deletedTimeISO), [all]);
}
